<template>
  <div
    ref="editableLabelWrapper"
    class="editable-label"
  >
    <p
      class="editable-label__label"
      v-text="label"
    />

    <b-form
      v-if="isEditable"
      v-show="isEdited"
      novalidate
      validated
      autocomplete="off"
      class="editable-label__form"
      @submit.prevent
    >
      <b-form-group
        :state="!errors.has($t('label.reference_number'))"
        class="editable-label__edit-wrapper"
      >
        <input
          ref="inputField"
          v-model="value"
          v-validate="{ rules: validationRules }"
          :data-testing="dataTesting + '_input'"
          name="reference_number"
          type="text"
          required
          class="editable-label__input"
          data-vv-validate-on="blur"
          onfocus="this.value = this.value;"
          @keydown.esc="onKeyupEsc"
          @keydown.enter.capture="submit"
        >
        <btn
          :data-testing="dataTesting + '_button_save'"
          variant="primary"
          icon="save"
          class="editable-label__button"
          @click="submit"
        >
          {{ $t('button.save') }}
        </btn>
        <btn
          :data-testing="dataTesting + '_button_cancel'"
          variant="secondary"
          icon="close"
          class="editable-label__button"
          @click="onClickCancel"
        >
          {{ $t('button.cancel') }}
        </btn>
      </b-form-group>
      <tooltip
        v-if="errors.items.length"
        :text="errors.items[0].msg"
        :is-visible="showErrorTooltip"
        class="editable-label__error"
        @close="onCloseTooltip"
      />
    </b-form>

    <p
      v-show="!isEdited"
      class="editable-label__number-wrapper"
    >
      <span
        ref="displayNumber"
        :data-testing="dataTesting + '_label'"
        class="editable-label__number"
        v-text="value"
      />
      <icon
        v-if="isEditable"
        :data-testing="dataTesting + '_edit_button'"
        icon="edit"
        color="red"
        color-hover="grey"
        size="19"
        emits-click-event
        class="editable-label__icon-edit"
        @click="onEditClick"
      />
    </p>
  </div>
</template>

<script>
import Vue from 'vue';
import { BFormGroup } from 'bootstrap-vue';
import { BForm } from 'bootstrap-vue';
import Icon from '@atoms/Icon/Icon';
import Tooltip from '@components/Tooltip/Tooltip';
import Btn from '@components/Btn/Btn';

/**
 * Component for displaying and editing a guarantee number.
 */
export default {
  name: 'EditableLabel',
  components: {
    BFormGroup,
    BForm,
    Icon,
    Tooltip,
    Btn
  },
  inject: {
    $validator: '$validator'
  },
  props: {
    /**
     * id of the editable label
     */
    id: {
      type: String,
      required: true
    },
    /**
    * label of the editable label
    */
    label: {
      type: String,
      default: ''
    },
    /**
     * actual editable label
     */
    number: {
      type: String,
      required: true
    },
    /**
     * sets a minimum length of the input value
     */
    minLength: {
      type: Number,
      default: null
    },
    /**
     * defines if the number is editable
     */
    isEditable: {
      type: Boolean,
      default: false
    },
    /**
     * Variable for cypress testing
     */
    dataTesting: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      /**
       * @model
       */
      value: '',
      valueCurrent: '',
      isEdited: false,
      showErrorTooltip: true
    };
  },

  computed: {
    validationRules() {
      let rules = {};

      if (this.minLength) {
        Vue.set(rules, 'min', this.minLength);
      }

      return rules;
    }
  },

  watch: {
    number(newVal) {
      this.value = newVal;
    },
    errors: {
      handler (newVal) {
        if (newVal.items.length && !this.showErrorTooltip) {
          this.showErrorTooltip = true;
        }
      },
      deep:true
    }
  },
  mounted() {
    this.value = this.number;
  },
  methods: {
    /**
     * @method submit
     * @type {submit}
     * Emits the value of the input field to parent
     */
    submit(event) {
      this.emitValue(event);
      event.stopPropagation();
    },

    /**
     * @method onKeyupEsc
     * @type {keyup}
     * Aborts the edit process and resets value
     */
    onKeyupEsc() {
      this.value = this.valueCurrent;
      this.isEdited = false;
    },

    /**
     * @method onEditClick
     * @type {click}
     * Makes the input field visible and allows the user the edit the value
     */
    onEditClick() {
      this.isEdited = true;
      this.valueCurrent = this.value;

      const input = this.$refs.inputField;

      this.$nextTick(() => {
        input.focus();
      });

      input.style.width = (this.$refs.displayNumber.clientWidth + 10) + 'px';
    },

    /**
     * @method onClickCancel
     * @type {click}
     * Revert editing value to previous state
     */
    onClickCancel() {
      this.value = this.valueCurrent;
      this.isEdited = false;
    },

    /**
     * @method onCloseTooltip
     * @type {click}
     * Close the toollip
     */
    onCloseTooltip() {
      this.showErrorTooltip = false;
    },

    /**
     * @method emitValue
     * Emits the value to the parent component and hides input field.
     */
    emitValue() {
      if (!this.errors.items.length) {
        this.$emit('change', this.value);
        this.isEdited = false;
      }
    }
  }
};

</script>

<style lang="scss">
  @import './EditableLabel';
</style>
